import { useEffect, useState } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { getTeamSettings, updateTeamSettings } from "services/team";
import Toast from "components/common/Toast";

// Sliders surfaced as "Coming soon" — only Specification is active today,
// matching the Configure Scoring Weightage modal.
const COMING_SOON_SLIDERS = [
  "Prosecution",
  "Precedential",
  "Dictionary",
  "Article Accuracy",
];

const getTeamId = () => {
  try {
    return JSON.parse(localStorage.getItem("selectedTeam") || "{}")
      ?.currentTeamId;
  } catch {
    return undefined;
  }
};

const ScoreSettings = () => {
  const [specification, setSpecification] = useState(90);
  const [isSaving, setIsSaving] = useState(false);

  // Load the team's current scoring weightage.
  useEffect(() => {
    const teamId = getTeamId();
    if (!teamId) return;
    (async () => {
      try {
        const res: any = await getTeamSettings(teamId);
        const settings = res?.data ?? res;
        if (typeof settings?.score_settings?.specification === "number") {
          setSpecification(settings.score_settings.specification);
        }
      } catch (error) {
        console.error("Error fetching score settings:", error);
      }
    })();
  }, []);

  const handleSave = async () => {
    const teamId = getTeamId();
    if (!teamId) return;
    setIsSaving(true);
    try {
      const res: any = await updateTeamSettings({
        team_id: teamId,
        score_settings: { specification },
      });
      // Reflect the persisted value returned by the API.
      const saved = res?.data ?? res;
      if (typeof saved?.score_settings?.specification === "number") {
        setSpecification(saved.score_settings.specification);
      }
      Toast.success("Success", "Scoring weightage saved");
    } catch (error) {
      console.error("Error saving score settings:", error);
      Toast.error("Error", "Failed to save scoring weightage");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mt-5 max-w-[620px]">
      <p className="text-sm text-[#475467] mb-5">
        Adjust how each factor contributes to the overall coverage score.
      </p>

      <div className="flex flex-col gap-y-4">
        {/* Specification (active) — inlined so the input keeps a stable
            identity across renders and stays draggable. */}
        <div className="mb-2.5">
          <div className="text-sm font-interRegular text-[#555F6D] mb-1.5">
            Specification
          </div>
          <div className="relative w-full cursor-default">
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={specification}
              onChange={(e) => setSpecification(parseInt(e.target.value))}
              className="w-full h-2 cursor-pointer"
              style={{
                WebkitAppearance: "none",
                background: `linear-gradient(to right, #1751D0 ${specification}%, #E5E7EB ${specification}%)`,
                borderRadius: "9999px",
              }}
            />
          </div>
          <div className="flex items-center justify-end">
            <span className="text-sm font-interMedium text-[#4F4D55]">
              {specification}%
            </span>
          </div>
        </div>

        {COMING_SOON_SLIDERS.map((name) => (
          <div className="mb-6 disabled-rc-slider relative" key={name}>
            <span className="text-xs font-interMedium text-[#1751D0] bg-[#b5ccff80] border border-[#1751D0] px-2 py-0.5 rounded-full inline-block -top-2.5 right-4 absolute">
              Coming soon
            </span>
            <p className="text-sm font-interRegular text-[#555F6D] mb-3">
              {name}
            </p>
            <Slider />
            <div className="flex items-center justify-end mt-2">
              <span className="text-sm font-interMedium text-[#4F4D55]">
                0%
              </span>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving}
        className={`submit-btn-blue !px-[15px] !text-sm hover:bg-[#120693] mt-2 ${
          isSaving ? "button-disabled" : ""
        }`}
      >
        {isSaving ? "Saving..." : "Save"}
      </button>
    </div>
  );
};

export default ScoreSettings;
